import type { SpeciesCode } from './types.ts';

/** Types shared by the account API and the web client. */

export type Role = 'user' | 'admin';

export interface HomeLocation {
  lat: number;
  lon: number;
  /** Free-text label, e.g. the address the user typed. */
  label: string | null;
}

export interface AccountUser {
  id: number;
  username: string;
  role: Role;
  home: HomeLocation | null;
  /** Set by an admin ("expire password"), not by age. True means the client
   * must force a password change before anything else — see App.tsx's gate. */
  passwordExpired: boolean;
}

/**
 * An account as an admin sees it in the account-management list — everyone
 * on the server, not just the signed-in one. No password, no home location:
 * an admin creating or reviewing accounts doesn't need either.
 */
export interface AdminAccount {
  id: number;
  username: string;
  role: Role;
  passwordExpired: boolean;
  createdAt: number;
}

/**
 * Saved views per account. Every one is also a quick-access button on the map
 * (on a phone, one equal-width row above the time bar), so the limit is what
 * fits comfortably in that row.
 */
export const MAX_SAVED_VIEWS = 4;

/** A named, restorable map view. */
export interface SavedView {
  id: number;
  name: string;
  lat: number;
  lon: number;
  zoom: number;
  trailHours: number;
  showViewing: boolean;
  hiddenSpecies: SpeciesCode[];
  /** The view loaded automatically when the page opens; at most one per account. */
  isDefault: boolean;
  createdAt: number;
}

export type ViewpointStatus = 'approved' | 'pending' | 'rejected';

/** A community-submitted view point, as seen by its submitter or an admin. */
export interface ViewpointSubmission {
  id: number;
  name: string;
  region: string | null;
  note: string | null;
  lat: number;
  lon: number;
  hike: boolean;
  status: ViewpointStatus;
  submittedBy: string | null;
  reviewNote: string | null;
  createdAt: number;
}

export interface MeResponse {
  user: AccountUser | null;
  registrationOpen: boolean;
}

/**
 * Nearby-whale alerts for one account: a push to each device that has alerts
 * turned on (there is no account-wide on/off — a device is on or it is not)
 * when a new report lands within `radiusMiles` of home, at most `maxCount`
 * notifications every `windowHours` (a sliding window, counted per account).
 */
export interface NotifySettings {
  radiusMiles: number;
  maxCount: number;
  windowHours: number;
}

export const NOTIFY_LIMITS = {
  radiusMiles: { min: 0.5, max: 100 },
  maxCount: { min: 1, max: 50 },
  windowHours: { min: 0.25, max: 168 },
} as const;

export interface NotificationsResponse {
  /** False when the server has no VAPID keys configured; the feature is hidden. */
  available: boolean;
  vapidPublicKey: string | null;
  settings: NotifySettings;
  /** Devices with alerts turned on for this account (across all browsers). Alerts are on when this is above 0. */
  deviceCount: number;
}

export interface GeocodeResult {
  label: string;
  lat: number;
  lon: number;
}
